import React from "react";

interface DialogWrapperProps {
  open: boolean;
  onClose: () => void;
  title: string;
  width?: number;
  children: React.ReactNode;
}

export function DialogWrapper({ open, onClose, title, width, children }: DialogWrapperProps) {
  if (!open) return null;

  return (
    <div className="dialog-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label={title}>
      <div className="dialog" style={width ? { width } : undefined} onClick={(e) => e.stopPropagation()}>
        <div className="dialog-title">{title}</div>
        <div className="dialog-content">{children}</div>
      </div>
    </div>
  );
}

interface GroupBoxProps {
  label: string;
  children: React.ReactNode;
}

export function GroupBox({ label, children }: GroupBoxProps) {
  return (
    <div className="groupbox">
      <span className="groupbox-label">{label}</span>
      {children}
    </div>
  );
}

interface ProgressBarProps {
  value: number;
  max?: number;
}

export function ProgressBar({ value, max = 100 }: ProgressBarProps) {
  const percent = Math.min(100, (value / max) * 100);
  return (
    <div className="progress-bar" role="progressbar" aria-valuenow={value} aria-valuemax={max}>
      <div className="progress-bar-fill" style={{ width: `${percent}%` }} />
    </div>
  );
}

interface ListControlProps<T> {
  columns: { key: string; label: string; width?: number }[];
  items: T[];
  selectedIndex?: number;
  onSelect?: (index: number) => void;
  /** Fixed pixel height, or a CSS string like "100%" to fill a flex parent. */
  height?: number | string;
  getRow: (item: T) => Record<string, string>;
  /** When set, columns are clickable; receives the column `key` on click. */
  onHeaderClick?: (key: string) => void;
  /** Currently sorted column key — shows ▲/▼ indicator next to its label. */
  sortKey?: string;
  /** Direction of the current sort. */
  sortDir?: "asc" | "desc";
}

export function ListControl<T>({
  columns,
  items,
  selectedIndex,
  onSelect,
  height = 150,
  getRow,
  onHeaderClick,
  sortKey,
  sortDir,
}: ListControlProps<T>) {
  return (
    <div className="list-control" style={{ height }}>
      <table>
        <thead>
          <tr>
            {columns.map((col) => {
              const isSorted = sortKey === col.key;
              const indicator = isSorted ? (sortDir === "desc" ? " ▼" : " ▲") : "";
              const style: React.CSSProperties = {
                ...(col.width ? { width: col.width } : {}),
                ...(onHeaderClick ? { cursor: "pointer", userSelect: "none" } : {}),
              };
              return (
                <th
                  key={col.key}
                  style={style}
                  onClick={onHeaderClick ? () => onHeaderClick(col.key) : undefined}
                >
                  {col.label}{indicator}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => {
            const row = getRow(item);
            return (
              <tr
                key={idx}
                className={selectedIndex === idx ? "selected" : ""}
                onClick={() => onSelect?.(idx)}
              >
                {columns.map((col) => (
                  <td key={col.key}>{row[col.key] ?? ""}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
